/**
 * MuelleLayout — Sidebar secundaria (desktop) / Tabs scrolleables (mobile)
 * para alternar entre Recepción y Cierre del día.
 * Mismo patrón que CatalogoLayout / BookingLayout / PasadiasLayout.
 */
import React, { useEffect } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  Box, Typography, List, ListItemButton, ListItemIcon, ListItemText,
  Tabs, Tab,
} from '@mui/material';
import AnchorIcon from '@mui/icons-material/Anchor';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

const SECTIONS = [
  { path: 'recepcion', label: 'Recepción', icon: <AnchorIcon />,      color: '#0d9488' },
  { path: 'cierre',    label: 'Cierre',    icon: <ReceiptLongIcon />, color: '#8b5cf6' },
];

function getUserRole() {
  try { return JSON.parse(localStorage.getItem('user') || '{}').role || 'miembro'; }
  catch { return 'miembro'; }
}

export default function MuelleLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const role = getUserRole();

  // Redirigir a "recepcion" si entran a /muelle sin sub-ruta
  useEffect(() => {
    if (location.pathname === '/muelle' || location.pathname === '/muelle/') {
      navigate('/muelle/recepcion', { replace: true });
    }
  }, [location.pathname, navigate]);

  // Filtrar secciones según rol (Cierre disponible para comercial+, todos pueden ver Recepción)
  const visibleSections = SECTIONS;
  const activeIdx = Math.max(0, visibleSections.findIndex(s => location.pathname.includes(`/muelle/${s.path}`)));

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', bgcolor: '#f8fafc' }}>
      {/* Sidebar secundario (desktop) */}
      <Box sx={{
        width: 240, flexShrink: 0, bgcolor: '#fff',
        borderRight: '1px solid #e2e8f0',
        display: { xs: 'none', md: 'flex' }, flexDirection: 'column',
      }}>
        <Box sx={{ p: 2.5, pb: 1.5 }}>
          <Typography sx={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
            Muelle
          </Typography>
        </Box>
        <List sx={{ px: 1, py: 0 }} dense>
          {visibleSections.map(s => {
            const isActive = location.pathname.includes(`/muelle/${s.path}`);
            return (
              <ListItemButton key={s.path} onClick={() => navigate(`/muelle/${s.path}`)}
                sx={{
                  borderRadius: 2, mb: 0.5, py: 0.75, pl: 1.5,
                  bgcolor: isActive ? `${s.color}15` : 'transparent',
                  borderLeft: isActive ? `3px solid ${s.color}` : '3px solid transparent',
                  '&:hover': { bgcolor: isActive ? `${s.color}25` : '#f1f5f9' },
                }}>
                <ListItemIcon sx={{ minWidth: 32, color: isActive ? s.color : '#94a3b8' }}>
                  {s.icon}
                </ListItemIcon>
                <ListItemText primary={s.label}
                  primaryTypographyProps={{
                    fontSize: 13, fontWeight: isActive ? 700 : 500,
                    color: isActive ? '#0f172a' : '#475569',
                  }} />
              </ListItemButton>
            );
          })}
        </List>

        <Box sx={{ flex: 1 }} />
        <Box sx={{ p: 2, borderTop: '1px solid #f1f5f9' }}>
          <Typography sx={{ fontSize: 10.5, color: '#94a3b8', lineHeight: 1.5 }}>
            Operación diaria del muelle: check-in, cobros, embarque y cierre de caja.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 0, md: 0 } }}>
        {/* Tabs móviles */}
        <Box sx={{
          display: { xs: 'block', md: 'none' },
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#fff',
          position: 'sticky', top: 0, zIndex: 5,
        }}>
          <Tabs
            value={activeIdx}
            onChange={(_, idx) => navigate(`/muelle/${visibleSections[idx].path}`)}
            variant="fullWidth"
            sx={{
              minHeight: 48,
              '& .MuiTab-root': { minHeight: 48, textTransform: 'none', fontWeight: 700, fontSize: 13 },
              '& .MuiTabs-indicator': { backgroundColor: visibleSections[activeIdx]?.color || '#0d9488', height: 3, borderRadius: 2 },
            }}
          >
            {visibleSections.map(s => (
              <Tab key={s.path} icon={s.icon} iconPosition="start" label={s.label} />
            ))}
          </Tabs>
        </Box>

        <Outlet />
      </Box>
    </Box>
  );
}
